// Importing candidates from a file — TODO T43 (6 Sep 2026).
//
// THE CONSOLE STILL WRITES NO CANDIDATE. An import is T38's request N times: the preview is a
// POST that writes nothing, the confirm files one `candidate_entries` row per ready row (and
// creates the clients and roles the rows name), and the settle call is the poll — which requests
// the worker has applied, each of those put on their role and their note recorded by the service
// as it answers. The file's text goes up once, for the preview, and is never stored anywhere.
//
// This module's mutations invalidate broadly, on purpose: an import moves the candidates list,
// the clients list, the searches list and every roster at once.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type {
  ImportConfirmResponse,
  ImportReading,
  ImportOptionsResponse,
  ImportPreviewResponse,
  ImportRowFields,
  ImportSettlement,
  ImportSettleResponse,
  RowEdit,
} from '@vera/bff';
import { API_PREFIX, type RequestFn } from './core.ts';
import { KEYS, useConsoleMutation } from './mutation.ts';
import { useRequest } from './provider.tsx';

export interface ImportPreviewInput {
  /** The file's name, for the record — "imported from list.csv". */
  name: string;
  /** A CSV's text, read in the browser — or a workbook (`.xlsx`) as base64. One of the two. */
  text?: string;
  workbook?: string;
  country: string | null;
  /**
   * Fill rows naming no client or role; a workbook's tabs name their own first. Ids PICKED from
   * the brain's lists since T113, never typed names.
   */
  defaults: { clientId: string | null; searchId: string | null };
  /**
   * T113a: names the preview called new, pointed by the recruiter at one in the brain — a client
   * by the name the file gave, a role by its client's id and the title the file gave.
   */
  links?: {
    clients: { name: string; clientId: string }[];
    roles: { clientId: string | null; title: string; searchId: string }[];
  };
  /**
   * T365a: how the file was read, as the last preview returned it and the dialog edited it, so a
   * re-preview keeps those edits and does not read the file again. Absent on the first preview.
   */
  reading?: ImportReading;
  /** T365c: what the recruiter changed on rows of the preview, sent with every re-preview. */
  edits?: RowEdit[];
}

export interface ImportConfirmInput {
  file: string;
  rows: ImportRowFields[];
}

/**
 * How long the settle poll keeps asking before the dialog says the rest will land on its own.
 * Not a timeout on the import — every request is filed and the worker applies them whenever it
 * next sweeps — but on the spinner (the same rule as api/candidateEntries.ts).
 */
export const IMPORT_POLL_TIMEOUT_MS = 5 * 60_000;
/** A person is watching this land, row by row. */
export const IMPORT_POLL_MS = 1_500;

export const importApi = (request: RequestFn) => ({
  options: () => request<ImportOptionsResponse>(`${API_PREFIX}/candidates/import/options`),
  // T160: both carry the page's release, so a page from another one is told to reload.
  preview: (input: ImportPreviewInput) =>
    request<ImportPreviewResponse>(`${API_PREFIX}/candidates/import/preview`, {
      method: 'POST',
      body: { ...input, release: request.release ?? null },
    }),
  confirm: (input: ImportConfirmInput) =>
    request<ImportConfirmResponse>(`${API_PREFIX}/candidates/import`, {
      method: 'POST',
      body: { ...input, release: request.release ?? null },
    }),
  settle: async (items: ImportSettlement[]) =>
    (
      await request<ImportSettleResponse>(`${API_PREFIX}/candidates/import/settle`, {
        method: 'POST',
        body: { items },
      })
    ).rows,
});

export function useImportOptions(enabled: boolean): UseQueryResult<ImportOptionsResponse> {
  const request = useRequest();
  return useQuery({
    queryKey: ['importOptions'],
    queryFn: () => importApi(request).options(),
    enabled,
    staleTime: Infinity,
  });
}

/** A preview is a mutation in shape — a POST with a body — and a read in effect: it invalidates nothing. */
export const useImportPreview = () =>
  useConsoleMutation((request, input: ImportPreviewInput) => importApi(request).preview(input), []);

export const useConfirmImport = () =>
  useConsoleMutation(
    (request, input: ImportConfirmInput) => importApi(request).confirm(input),
    KEYS.import,
  );

export const useSettleImport = () =>
  useConsoleMutation(
    (request, items: ImportSettlement[]) => importApi(request).settle(items),
    KEYS.import,
  );
